import { formatMoney } from 'accounting';
import parse from 'html-react-parser';
import { isEmpty } from 'lodash';
import sanitizeHtml from 'sanitize-html';

import { RichTextContent } from 'ContentfulDefaults/types/_shared';
import { PlanCustomization } from 'ContentfulDefaults/types/customizations';
import {
  DoctorType,
  LocationProps,
  ProviderLocationProps,
  Recommendation,
  RxTierCost,
  RxTierGroup,
  RxTierNames,
  SupplementalPlan,
  SupplementalPlansFullHousehold,
} from 'Types/entities';
import Logger from 'Utils/logger';

// Returns true when arrays do not contain identical elements (order not considered)
export const checkArraysForSymetricDifference = (arrOne, arrTwo) => {
  const differenceOne = arrOne.filter((i) => !arrTwo.includes(i));
  const differenceTwo = arrTwo.filter((j) => !arrOne.includes(j));
  return [...differenceOne, ...differenceTwo].length > 0;
};

// given Contentful RichText Object, returns all the string values concatenated into one string
export const getRichTextAsString = (rtObject: RichTextContent, vars: string[] = []) => {
  let parsed = ``;
  let varsIndex = 0;
  rtObject?.content?.forEach((i) => {
    i.content?.forEach((j) => {
      if (j.value) {
        if (!!vars.length && j.value.includes('{x}')) {
          parsed += `${j.value.replace(/{x}/, vars[varsIndex])}`;
          varsIndex += 1;
        } else {
          parsed += `${j.value}`;
        }
      }
    });
  });
  return parsed;
};

// given Contentful RichText Object, returns all the string values as a 1D string array
export const getRichTextAsStringArray = (rtObject: RichTextContent, vars: string[] = []) => {
  const parsed: string[] = [];
  let varsIndex = 0;
  rtObject.content?.forEach((i) => {
    i.content?.forEach((j) => {
      if (j.value) {
        if (!!vars.length && j.value.includes('{x}')) {
          parsed.push(`${j.value.replace(/{x}/, vars[varsIndex])}`);
          varsIndex += 1;
        } else {
          parsed.push(`${j.value}`);
        }
      }
    });
  });
  return parsed;
};

// Reads text at any depth, so a document whose only text sits inside a link or a list still counts as non-empty.
const richTextNodeToString = (node: { value?: string; content?: unknown[] } | undefined): string =>
  (node?.value ?? '') +
  (node?.content ?? [])
    .map((child) => richTextNodeToString(child as { value?: string; content?: unknown[] }))
    .join('');

export const getIsRichTextEmpty = (rtObject: RichTextContent): boolean =>
  richTextNodeToString(rtObject) === '';

export const parseSanitizeHtml = (dirty: string) => parse(sanitizeHtml(dirty));

// Sanitizes HTML and adaptively shifts heading levels so the most prominent heading maps to h4,
// matching Contentful's maxHeaderSize=4 adaptive behavior (e.g. h1→h4, or h2→h4 when no h1 exists).
export const shiftHtmlHeadingLevels = (dirty: string): string => {
  const topHeadingLevel = [1, 2, 3, 4, 5, 6].find((n) => dirty.includes(`<h${n}`)) ?? 4;
  const shift = Math.max(0, 4 - topHeadingLevel);
  if (shift === 0) return sanitizeHtml(dirty);
  return sanitizeHtml(dirty, {
    transformTags: Object.fromEntries([1, 2, 3, 4, 5, 6].map((n) => [`h${n}`, `h${Math.min(n + shift, 6)}`])),
  });
};

export const parseSanitizeHtmlShiftHeadings = (dirty: string) => parse(shiftHtmlHeadingLevels(dirty));

export const toDollars = (dollars: string | number | null | undefined, precision?: number) => {
  // Convert to number - if it's already a number, use it; otherwise parse the string
  const numericValue = typeof dollars === 'number' ? dollars : Number(dollars);

  // If the value is not a valid number, return $0
  if (Number.isNaN(numericValue)) {
    // Log warnings for invalid values
    if (typeof dollars === 'string') {
      Logger.warn(`Invalid number string "${dollars}" passed to toDollars, returning $0`);
    } else if (typeof dollars === 'number') {
      Logger.warn(`NaN value passed to toDollars, returning $0`);
    }
    return formatMoney(0, '$', 0);
  }

  // If precision is not explicitly provided, auto-detect based on whether value is an integer
  let finalPrecision = precision;
  if (finalPrecision === undefined) {
    finalPrecision = Number.isInteger(numericValue) ? 0 : 2;
  }

  return formatMoney(numericValue, '$', finalPrecision);
};

export const formatDate = (
  date: string | Date,
  locale: string,
  options = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  } as Intl.DateTimeFormatOptions,
) => new Date(date).toLocaleDateString(locale, options);

export const isValidZipCode = (zip?: string): boolean =>
  zip !== undefined && zip !== '00000' && !!zip.toString().match(/^[0-9]{5}$/);

export const getProviderAddress = (addressDetail): string => {
  const { street, city, state, zip } = addressDetail || {};

  // Create an array for the address parts, with state and zip combined
  const addressParts: string[] = [];

  if (street) addressParts.push(street);
  if (city) addressParts.push(city);
  if (state || zip) addressParts.push([state, zip].filter(Boolean).join(' '));

  return addressParts.join(', ');
};

export const createProviderData = (result: DoctorType): ProviderLocationProps => {
  const label = [result.first_name, result.middle_name, result.last_name].filter(Boolean).join(' ');
  const providerTypes = result.provider_types;
  const specialties = result.specialties;
  const locations: LocationProps[] = result.locations.map((location) => {
    const address = getProviderAddress(location!.address_details);
    const { uuid, name, distance, phone_numbers, accepting_new_patients, group_name } = location;
    return {
      uuid,
      name,
      address,
      distance,
      phoneNumbers: phone_numbers,
      value: result.npi,
      isAcceptingPatients: accepting_new_patients,
      group_name,
    };
  });

  return {
    label,
    locations,
    providerTypes,
    value: result.npi,
    specialties,
  };
};

/**
 * Determines if preferred network providers should be shown and retrieves the most preferred provider name if applicable.
 * @param recommendation - Recommendation object containing plan details
 * @param shouldShowPreferredProviderTiers - Flag indicating if preferred provider tiers should be shown
 * @returns Object containing isMostPreferredEnabled flag and mostPreferredProviderName
 */
export const getPreferredNetworkProviderName = (
  recommendation: Recommendation | undefined,
  shouldShowPreferredProviderTiers: boolean,
): {
  isMostPreferredEnabled: boolean;
  mostPreferredProviderName: string | null;
} => {
  const { use_custom_tier_name, custom_tier_name } = recommendation?.plan || {};
  const isMostPreferredEnabled =
    !!use_custom_tier_name && !!custom_tier_name && !!shouldShowPreferredProviderTiers;
  return {
    isMostPreferredEnabled,
    mostPreferredProviderName: isMostPreferredEnabled ? custom_tier_name : null,
  };
};

/**
 * Detects whether a plan is a user-entered custom plan.
 * Custom plans are ephemeral,
 * exist only for the current session, and are not stored in JVP's database.
 * When selected, they are treated as waived elections in PUT /elections.
 *
 * Detection is based on:
 * - plan_type === 'Custom' (set by transform_ephemeral_plan in the backend)
 * - external_id starting with 'online_' (generated by the ephemeral_plan endpoint)
 * - external_plan_id starting with 'online_'
 *
 */
export const isCustomPlan = (plan: Recommendation | Record<string, never> | null | undefined): boolean => {
  if (!plan || !('plan' in plan)) return false;
  const { plan: planData } = plan as Recommendation;
  return Boolean(
    planData.plan_type === 'Custom' ||
      planData.external_id?.startsWith('online_') ||
      planData.external_plan_id?.startsWith('online_'),
  );
};

export const getRxFieldAndVars = (tier: RxTierCost | null | undefined) => {
  if (!tier) return null;

  const { payment_type, copay, coinsurance, has_maximum_group, minimum } = tier;
  const calculatedCoinsuranceAmount = coinsurance != null ? 100 - coinsurance : null;

  switch (payment_type) {
    case 'Copay':
      return {
        field: 'health_details_section.rx_copay',
        vars: [`$${copay}`],
      };

    case 'Coins':
      return {
        field: 'health_details_section.rx_coinsurance',
        vars: [`${calculatedCoinsuranceAmount}%`],
      };

    case 'CoinsWithLimits':
      if (has_maximum_group?.has_maximum && has_maximum_group?.maximum != null && minimum != null) {
        // show both min and max value
        return {
          field: 'health_details_section.rx_coinsurance_with_limits_having_min_max',
          vars: [`${calculatedCoinsuranceAmount}%`, `$${minimum}`, `$${has_maximum_group?.maximum}`],
        };
      }

      if (has_maximum_group?.has_maximum && has_maximum_group?.maximum != null && minimum == null) {
        // No min values, show max value only
        return {
          field: 'health_details_section.rx_coinsurance_with_limits_without_min',
          vars: [`${calculatedCoinsuranceAmount}%`, `$${has_maximum_group?.maximum}`],
        };
      }

      if (
        (!has_maximum_group?.has_maximum && minimum != null) ||
        (has_maximum_group?.has_maximum && has_maximum_group?.maximum == null && minimum != null) // edge case logic
      ) {
        // No max values, show min value only
        return {
          field: 'health_details_section.rx_coinsurance_with_limits_without_max',
          vars: [`${calculatedCoinsuranceAmount}%`, `$${minimum}`],
        };
      }

      // returns null if we don't have min and max values
      return null;

    case 'CopayThenCoins':
      return {
        field: 'health_details_section.rx_copay_then_coinsurance',
        vars: [`$${copay}`, `${calculatedCoinsuranceAmount}%`],
      };

    case 'NoCoverage':
      return {
        field: 'health_details_section.rx_tiers_static_values.no_coverage',
        isText: true,
      };
    default:
      return null;
  }
};

/**
 * Resolves the label for one Rx tier, for whichever network is being displayed.
 *
 * Contentful is preferred over the API's `tier_name` because it is the localized source,
 * while the API carries Builder's English config. Each network has its own Contentful
 * names — `rx_tier` in-network, `rx_tier_oon` out-of-network — and out-of-network falls
 * back to the in-network Contentful name as a last resort: better an in-network label
 * than an unlabeled row.
 *
 * `retrieveContentfulData` is passed in rather than read from context so this stays a
 * pure function. Shared by the plan details Rx card and the compare modal, which must
 * label a tier identically.
 */
export const getRxTierName = ({
  retrieveContentfulData,
  planId,
  tierKey,
  tierGroup,
  isOutOfNetwork,
}: {
  retrieveContentfulData: <T>(key: string, defaultValue?: T) => T;
  planId: string;
  tierKey: keyof RxTierNames;
  tierGroup: RxTierGroup | null;
  isOutOfNetwork: boolean;
}): string | undefined => {
  const customizations = retrieveContentfulData<PlanCustomization[]>('plan_customizations', []);
  const planIndex = customizations.findIndex((item) => item.plan_id === planId);
  const planRxName = retrieveContentfulData<RxTierNames>(`plan_customizations[${planIndex}].rx_tier`);

  const apiTierName = tierGroup?.[tierKey]?.tier_name;
  const inNetworkContentfulName = planRxName?.[tierKey]?.name;

  if (!isOutOfNetwork) {
    return inNetworkContentfulName || apiTierName;
  }

  const planRxNameOon = retrieveContentfulData<RxTierNames>(`plan_customizations[${planIndex}].rx_tier_oon`);

  return planRxNameOon?.[tierKey]?.name || apiTierName || inNetworkContentfulName;
};

export const isDeductibleWaived = (tier: RxTierCost | null | undefined) => {
  if (!tier) return null;

  const { is_rx_deductible_waived } = tier;

  if (is_rx_deductible_waived === null) return null;

  let field = '';
  if (is_rx_deductible_waived) {
    field = 'health_details_section.rx_tiers_static_values.deductible_waived';
  } else {
    field = 'health_details_section.rx_tiers_static_values.after_deductible';
  }

  return field;
};

export const shouldDisplayHsaText = (
  hsaBalance: number | undefined,
  hasOopCost: boolean,
  is_hsa_funds_question_enabled: boolean | undefined,
  is_send_hsa_funds_to_comm_enabled: boolean | undefined,
) => {
  const isHsaBalanceAvailable = Boolean(hsaBalance && hsaBalance > 0);

  /* Display new HSA Text only when : 
   - hasOopCost is true (HSA enabled from builder)
   - HSA balance is greater than 0
   - HSA Feature Flag is turned ON 
   - Sending HSA fund to COMM-API Feature Flag is turned ON */
  return Boolean(
    hasOopCost && isHsaBalanceAvailable && is_hsa_funds_question_enabled && is_send_hsa_funds_to_comm_enabled,
  );
};

export type FsaBanner = 'none' | 'eligible' | 'limited_purpose';

export type FsaType = 'none' | 'fsa' | 'limited_purpose' | 'standalone' | null;

export interface FsaDecision {
  buttonVisible: boolean;
  banner: FsaBanner;
  fsaType: FsaType;
  /**
   * The specific FSA plan whose contribution/rollover data should be displayed,
   * or null when there's no specific linked plan to point at (in which case the
   * federal statutory limit disclaimer copy should be used instead).
   */
  fsaPlan: SupplementalPlan | null;
  /**
   * True only for the standalone-via-tax-savings-plan case where the selected
   * medical plan also has a linked HSA - the FSA still exists, but can only be
   * used for dental/vision expenses.
   */
  isDentalVisionOnly: boolean;
}

export interface GetFsaDecisionArgs {
  hasSelectedPlan: boolean;
  planHasLinkedHsa: boolean;
  planLinkedFsaPlan: SupplementalPlan | null;
  taxSavingsLinkedFsaPlan: SupplementalPlan | null;
  /** True when the household has an fsa_medical election on record, independent of which
   *  plan "wins" for display purposes (unlike `taxSavingsLinkedFsaPlan`, which is nulled out
   *  when `planLinkedFsaPlan` is present). A plan merely *offering* a linked FSA isn't proof
   *  the household actually has one - only an election is, so this alone (together with
   *  `eligibleForAnyPlanWithFsa`) gates `buttonVisible` for the plan-linked-FSA case. */
  hasHouseholdMedicalFsaElection: boolean;
  eligibleForAnyPlanWithFsa: boolean;
}

const NO_FSA_BUTTON: FsaDecision = {
  buttonVisible: false,
  banner: 'none',
  fsaType: null,
  fsaPlan: null,
  isDentalVisionOnly: false,
};

const GENERIC_FSA_ELIGIBLE: FsaDecision = {
  buttonVisible: true,
  banner: 'eligible',
  fsaType: null,
  fsaPlan: null,
  isDentalVisionOnly: false,
};

/**
 * `planLinkedFsaPlan` and `taxSavingsLinkedFsaPlan` are normally mutually exclusive.
 * When both are populated, `planLinkedFsaPlan` must win: it belongs to the plan the
 * member actually selected, while `taxSavingsLinkedFsaPlan` is only a household fallback.
 */
export const getFsaDecision = ({
  hasSelectedPlan,
  planHasLinkedHsa,
  planLinkedFsaPlan,
  taxSavingsLinkedFsaPlan,
  hasHouseholdMedicalFsaElection,
  eligibleForAnyPlanWithFsa,
}: GetFsaDecisionArgs): FsaDecision => {
  if (!hasSelectedPlan) {
    if (!eligibleForAnyPlanWithFsa && !taxSavingsLinkedFsaPlan) {
      return NO_FSA_BUTTON;
    }
    return GENERIC_FSA_ELIGIBLE;
  }

  if (planLinkedFsaPlan) {
    return {
      buttonVisible: hasHouseholdMedicalFsaElection || eligibleForAnyPlanWithFsa,
      banner: planHasLinkedHsa ? 'limited_purpose' : 'eligible',
      fsaType: planHasLinkedHsa ? 'limited_purpose' : 'fsa',
      fsaPlan: planLinkedFsaPlan,
      isDentalVisionOnly: false,
    };
  }

  if (taxSavingsLinkedFsaPlan) {
    return {
      buttonVisible: true,
      banner: planHasLinkedHsa ? 'none' : 'eligible',
      fsaType: 'standalone',
      fsaPlan: taxSavingsLinkedFsaPlan,
      isDentalVisionOnly: planHasLinkedHsa,
    };
  }

  if (eligibleForAnyPlanWithFsa) {
    return {
      buttonVisible: true,
      banner: 'none',
      fsaType: 'none',
      fsaPlan: null,
      isDentalVisionOnly: false,
    };
  }

  return NO_FSA_BUTTON;
};

export const getFsaDecisionForSelectedPlan = (
  selectedPlan: Recommendation | Record<string, never>,
  supplementalPlans: SupplementalPlansFullHousehold,
): FsaDecision => {
  const hasSelectedPlan = !isEmpty(selectedPlan);
  const planLinkedFsaPlan = selectedPlan?.plan_details?.flexible_savings_account?.supplemental_plan ?? null;
  const fsaMedicalPlan = supplementalPlans?.fsa_medical?.plans?.[0] ?? null;

  return getFsaDecision({
    hasSelectedPlan,
    planHasLinkedHsa: hasSelectedPlan && !!selectedPlan?.plan?.hsa_eligible,
    planLinkedFsaPlan,
    taxSavingsLinkedFsaPlan: planLinkedFsaPlan ? null : fsaMedicalPlan,
    hasHouseholdMedicalFsaElection: !!fsaMedicalPlan,
    eligibleForAnyPlanWithFsa:
      !!supplementalPlans?.fsa_dependent?.plans?.length || !!supplementalPlans?.fsa_adoption?.plans?.length,
  });
};

export const isLimitedPurposeFsaForSelectedPlan = (
  selectedHealthPlan: Recommendation | Record<string, never>,
  supplementalPlans: SupplementalPlansFullHousehold,
): boolean => {
  const decision = getFsaDecisionForSelectedPlan(selectedHealthPlan, supplementalPlans);

  // A dental/vision-only standalone FSA is functionally the same restriction as a
  // plan-linked Limited Purpose FSA, so it must show the same Limited Purpose copy.
  return decision.fsaType === 'limited_purpose' || decision.isDentalVisionOnly;
};
